import React, { useState } from 'react';

const Policies = () => {
  // Sample policy data with mock PDF content
  const [policies, setPolicies] = useState([
    {
      id: 1,
      name: "Leave Policy 2025.pdf",
      category: "HR Policies",
      uploadDate: "2025-01-15",
      size: "2.4 MB",
      content: `
        <div style="padding: 20px; font-family: Arial, sans-serif;">
          <h1 style="color: #3b82f6; text-align: center;">Leave Policy 2025</h1>
          <h2>1. Introduction</h2>
          <p>This policy outlines the guidelines and procedures for employee leave at our company.</p>
          
          <h2>2. Types of Leave</h2>
          <h3>2.1 Annual Leave</h3>
          <p>Full-time employees are entitled to 20 days of annual leave per calendar year.</p>
          
          <h3>2.2 Sick Leave</h3>
          <p>Employees are entitled to 10 days of paid sick leave per year.</p>
          
          <h3>2.3 Maternity/Paternity Leave</h3>
          <p>New parents are entitled to 12 weeks of paid leave.</p>
          
          <h2>3. Request Procedure</h2>
          <p>All leave requests must be submitted through the HR portal at least 2 weeks in advance.</p>
        </div>
      `
    },
    {
      id: 2,
      name: "Employee Handbook.pdf",
      category: "General",
      uploadDate: "2024-11-30",
      size: "5.1 MB",
      content: `
        <div style="padding: 20px; font-family: Arial, sans-serif;">
          <h1 style="color: #3b82f6; text-align: center;">Employee Handbook</h1>
          <h2>Welcome to Our Company</h2>
          <p>This handbook provides information about our company's culture, policies, and procedures.</p>
          
          <h2>Company Values</h2>
          <ul>
            <li>Integrity in all business dealings</li>
            <li>Respect for all individuals</li>
            <li>Commitment to excellence</li>
            <li>Innovation and creativity</li>
          </ul>
          
          <h2>Work Hours</h2>
          <p>Standard work hours are from 9:00 AM to 5:00 PM, Monday through Friday.</p>
          
          <h2>Dress Code</h2>
          <p>Business casual attire is expected unless otherwise specified for client meetings.</p>
        </div>
      `
    },
    {
      id: 3,
      name: "IT Security Guidelines.pdf",
      category: "IT Policies",
      uploadDate: "2025-02-10",
      size: "3.7 MB",
      content: `
        <div style="padding: 20px; font-family: Arial, sans-serif;">
          <h1 style="color: #3b82f6; text-align: center;">IT Security Guidelines</h1>
          <h2>1. Password Policy</h2>
          <p>All employees must use strong passwords that are changed every 90 days.</p>
          
          <h2>2. Data Protection</h2>
          <p>Sensitive company data must be encrypted and stored only on approved devices.</p>
          
          <h2>3. Email Security</h2>
          <p>Be cautious of phishing emails. Do not click on suspicious links or attachments.</p>
          
          <h2>4. Remote Access</h2>
          <p>When working remotely, always use the company VPN to access internal resources.</p>
          
          <h2>5. Incident Reporting</h2>
          <p>Report any security incidents immediately to the IT department.</p>
        </div>
      `
    },
    {
      id: 4,
      name: "Code of Conduct.pdf",
      category: "HR Policies",
      uploadDate: "2024-09-22",
      size: "1.8 MB",
      content: `
        <div style="padding: 20px; font-family: Arial, sans-serif;">
          <h1 style="color: #3b82f6; text-align: center;">Code of Conduct</h1>
          <h2>Professional Behavior</h2>
          <p>All employees are expected to maintain professional behavior in the workplace.</p>
          
          <h2>Anti-Discrimination Policy</h2>
          <p>Our company prohibits discrimination based on race, gender, religion, age, or sexual orientation.</p>
          
          <h2>Confidentiality</h2>
          <p>Employees must maintain the confidentiality of company information.</p>
          
          <h2>Conflict of Interest</h2>
          <p>Employees must avoid situations that create a conflict between personal and company interests.</p>
        </div>
      `
    },
    {
      id: 5,
      name: "Work From Home Policy.pdf",
      category: "HR Policies",
      uploadDate: "2025-03-05",
      size: "2.1 MB",
      content: `
        <div style="padding: 20px; font-family: Arial, sans-serif;">
          <h1 style="color: #3b82f6; text-align: center;">Work From Home Policy</h1>
          <h2>1. Eligibility</h2>
          <p>Employees with at least 6 months of service are eligible for remote work arrangements.</p>
          
          <h2>2. Equipment</h2>
          <p>The company will provide necessary equipment for remote work, including laptop and accessories.</p>
          
          <h2>3. Work Hours</h2>
          <p>Remote employees are expected to be available during core business hours (10 AM - 4 PM).</p>
          
          <h2>4. Communication</h2>
          <p>Employees must maintain regular communication with their team via email, chat, and video calls.</p>
          
          <h2>5. Home Office Requirements</h2>
          <p>Employees must have a dedicated, distraction-free workspace for remote work.</p>
        </div>
      `
    }
  ]);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [currentPdf, setCurrentPdf] = useState(null);
  const [newPolicy, setNewPolicy] = useState({
    name: "",
    category: "HR Policies",
    file: null,
    content: ""
  });

  // Get unique categories
  const categories = ["All", ...new Set(policies.map(policy => policy.category))];

  // Filter policies based on search term and category
  const filteredPolicies = policies.filter(policy => {
    const matchesSearch = policy.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || policy.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Calculate file size in MB
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1) + " MB";
      
      // Set the file and auto-fill the name if empty
      setNewPolicy({
        ...newPolicy,
        file: file,
        name: newPolicy.name || file.name
      });
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (newPolicy.name && newPolicy.file) {
      // Create a new policy object
      const newPolicyObj = {
        id: policies.length + 1,
        name: newPolicy.name,
        category: newPolicy.category,
        uploadDate: new Date().toISOString().split('T')[0],
        size: (newPolicy.file.size / (1024 * 1024)).toFixed(1) + " MB",
        content: `
          <div style="padding: 20px; font-family: Arial, sans-serif;">
            <h1 style="color: #3b82f6; text-align: center;">${newPolicy.name.replace('.pdf', '')}</h1>
            <p>This is a newly uploaded policy document. In a real application, this would contain the actual PDF content.</p>
          </div>
        `
      };
      
      // Add to policies list
      setPolicies([...policies, newPolicyObj]);
      
      // Reset form and close modal
      setNewPolicy({
        name: "",
        category: "HR Policies",
        file: null,
        content: ""
      });
      setShowUploadModal(false);
    }
  };

  // Handle view policy
  const handleViewPolicy = (policy) => {
    setCurrentPdf(policy);
    setShowPdfModal(true);
  };

  // Handle download policy
  const handleDownloadPolicy = (policy) => {
    // Create a blob from the HTML content
    const blob = new Blob([policy.content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    // Create a temporary anchor element
    const a = document.createElement('a');
    a.href = url;
    a.download = policy.name;
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-indigo-800 mb-2 flex items-center justify-center">
            <span className="mr-3">📑</span> Company Policies
          </h1>
          <p className="text-gray-600">Central repository for all company policies and documents</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 flex items-center">
            <div className="rounded-full bg-blue-100 p-4 mr-4">
              <span className="text-2xl text-blue-600">📄</span>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-800">{policies.length}</h3>
              <p className="text-gray-600">Total Policies</p>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 flex items-center">
            <div className="rounded-full bg-green-100 p-4 mr-4">
              <span className="text-2xl text-green-600">🔄</span>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-800">Latest Update</h3>
              <p className="text-gray-600">March 2025</p>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 flex items-center">
            <div className="rounded-full bg-purple-100 p-4 mr-4">
              <span className="text-2xl text-purple-600">👥</span>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-800">Company-wide</h3>
              <p className="text-gray-600">All Employees</p>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Search policies..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="absolute right-3 top-3 text-gray-400">🔍</span>
            </div>
            
            <select
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            
            <button 
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 transition-all flex items-center"
              onClick={() => setShowUploadModal(true)}
            >
              <span className="mr-2">📤</span> Upload New
            </button>
          </div>
        </div>

        {/* Policies List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-gray-50 text-gray-600 font-semibold">
              <div className="col-span-6">Policy Name</div>
              <div className="col-span-3">Category</div>
              <div className="col-span-2">Upload Date</div>
              <div className="col-span-1">Actions</div>
            </div>
          </div>
          
          <div className="divide-y divide-gray-100">
            {filteredPolicies.length > 0 ? (
              filteredPolicies.map(policy => (
                <div key={policy.id} className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-blue-50 transition-colors">
                  <div className="col-span-6 flex items-center">
                    <span className="text-2xl mr-3">📄</span>
                    <div>
                      <div className="font-medium text-gray-800">{policy.name}</div>
                      <div className="text-sm text-gray-500">{policy.size}</div>
                    </div>
                  </div>
                  <div className="col-span-3 flex items-center">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {policy.category}
                    </span>
                  </div>
                  <div className="col-span-2 flex items-center text-gray-600">
                    {policy.uploadDate}
                  </div>
                  <div className="col-span-1 flex items-center justify-end space-x-2">
                    <button 
                      className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-full transition-colors" 
                      title="View"
                      onClick={() => handleViewPolicy(policy)}
                    >
                      👁️
                    </button>
                    <button 
                      className="p-2 text-green-600 hover:bg-green-100 rounded-full transition-colors" 
                      title="Download"
                      onClick={() => handleDownloadPolicy(policy)}
                    >
                      ⬇️
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-12 text-center">
                <div className="text-5xl mb-4">📭</div>
                <h3 className="text-xl font-medium text-gray-700 mb-2">No policies found</h3>
                <p className="text-gray-500">Try adjusting your search or filters</p>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-md p-6 text-white">
          <h2 className="text-2xl font-bold mb-4 flex items-center">
            <span className="mr-2">ℹ️</span> About Company Policies
          </h2>
          <p className="mb-3">This portal serves as the central repository for all company-wide documents and policies.</p>
          <ul className="list-disc list-inside space-y-2">
            <li>Upload and manage company policies (Leave Policy, HR Policy, Work From Home Policy, etc.)</li>
            <li>Employees can view and download the latest policies</li>
            <li>Ensures everyone has access to the current version of all documents</li>
            <li>Maintains document version history and update notifications</li>
          </ul>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-indigo-800 mb-6 flex items-center">
              <span className="mr-2">📤</span> Upload New Policy
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Policy Name</label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={newPolicy.name}
                  onChange={(e) => setNewPolicy({...newPolicy, name: e.target.value})}
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Category</label>
                <select
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={newPolicy.category}
                  onChange={(e) => setNewPolicy({...newPolicy, category: e.target.value})}
                  required
                >
                  <option value="HR Policies">HR Policies</option>
                  <option value="IT Policies">IT Policies</option>
                  <option value="General">General</option>
                  <option value="Finance">Finance</option>
                  <option value="Operations">Operations</option>
                </select>
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 mb-2">Policy Document (PDF)</label>
                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <span className="text-2xl mb-2">📎</span>
                      <p className="text-sm text-gray-500">
                        {newPolicy.file ? newPolicy.file.name : "Click to upload PDF"}
                      </p>
                    </div>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept=".pdf"
                      onChange={handleFileChange}
                      required
                    />
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                  onClick={() => setShowUploadModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  Upload Policy
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {showPdfModal && currentPdf && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-5/6 flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-xl font-semibold text-gray-800">{currentPdf.name}</h3>
              <button 
                className="text-gray-500 hover:text-gray-700 text-2xl"
                onClick={() => setShowPdfModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              <div className="w-full h-full bg-gray-100 rounded-lg overflow-hidden">
                {/* PDF Header */}
                <div className="bg-white p-4 border-b flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-2xl mr-2">📄</span>
                    <span className="font-medium">{currentPdf.name}</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {currentPdf.size} • Uploaded: {currentPdf.uploadDate}
                  </div>
                </div>
                
                {/* PDF Content */}
                <div className="bg-white p-4 h-full overflow-auto">
                  <div 
                    className="pdf-content bg-white p-6 rounded-lg border"
                    dangerouslySetInnerHTML={{ __html: currentPdf.content }}
                  />
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end space-x-3">
              <button 
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                onClick={() => setShowPdfModal(false)}
              >
                Close
              </button>
              <button 
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
                onClick={() => handleDownloadPolicy(currentPdf)}
              >
                <span className="mr-2">⬇️</span> Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Policies;
